import { useInvitations } from '@services/applications/queries/useInvitations'
import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useIntlLocale } from '@/i18n/languages'

type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED'

const STATUS_STYLES: Record<InvitationStatus, string> = {
  ACCEPTED: 'bg-primary/15 text-primary',
  PENDING: 'bg-peach/20 text-[#b5651d]',
  REJECTED: 'bg-destructive/10 text-destructive'
}

function initials(email?: string) {
  if (!email) return '?'
  return email.slice(0, 2).toUpperCase()
}

export function Invitation() {
  const { data, isLoading } = useInvitations()
  const { t } = useTranslation('application')
  const navigate = useNavigate()
  const locale = useIntlLocale()

  if (isLoading) return null
  if (!data || data.results.length === 0) return null

  return (
    <div className="mb-6 mt-10">
      <h2 className="m-0 mb-5 text-center font-display text-xl font-bold tracking-tight text-ink">
        {t('form.sections.roommates.invitationTitle')}
      </h2>
      <Card className="divide-y divide-ink/[0.06] border-0 p-0">
        {data.results.map((invitation) => {
          const status = (invitation.status ?? 'PENDING') as InvitationStatus
          const invitedOn = invitation.created_at
            ? new Date(invitation.created_at).toLocaleDateString(locale)
            : null

          return (
            <button
              key={invitation.id}
              type="button"
              onClick={() =>
                navigate(`/user/application/invitation/${invitation.id}`, {
                  state: {
                    id: invitation.id,
                    invitor: invitation.invitor_email,
                    status: invitation.status,
                    data: invitation.invitor_application
                  }
                })
              }
              className="group flex w-full items-center gap-4 border-0 bg-transparent p-5 text-left outline-none transition-colors first:rounded-t-3xl last:rounded-b-3xl hover:bg-ink/[0.02] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 font-display text-sm font-bold text-primary">
                {initials(invitation.invitor_email)}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-semibold text-ink">
                    {invitation.invitor_email}
                  </span>
                  <span
                    className={cn(
                      'inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold',
                      STATUS_STYLES[status]
                    )}
                  >
                    {t(`form.common.status.${status}`)}
                  </span>
                </div>
                <p className="m-0 mt-0.5 truncate text-sm text-muted-foreground">
                  {t('form.sections.roommates.invitedYou')}
                  {invitedOn &&
                    ` · ${t('form.sections.roommates.invitedOn', { date: invitedOn })}`}
                </p>
              </div>

              <span className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-ink/60 transition-colors group-hover:text-primary">
                <span className="hidden sm:inline">
                  {t('form.common.viewMore')}
                </span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </button>
          )
        })}
      </Card>
    </div>
  )
}
